import type { GameMeta, GameType, HowToPlay, Tutorial } from '@puzzlio/core';
import { meta as stars } from '@puzzlio/stars/config';
import { meta as fields } from '@puzzlio/fields/config';
import { meta as loopyRiver } from '@puzzlio/loopy-river/config';
import { meta as mosaic } from '@puzzlio/mosaic/config';
import { howToPlay as starsHowToPlay } from '@puzzlio/stars/howToPlay';
import { howToPlay as fieldsHowToPlay } from '@puzzlio/fields/howToPlay';
import { howToPlay as loopyRiverHowToPlay } from '@puzzlio/loopy-river/howToPlay';
import { howToPlay as mosaicHowToPlay } from '@puzzlio/mosaic/howToPlay';
import { tutorial as starsTutorial } from '@puzzlio/stars/tutorial';
import { tutorial as fieldsTutorial } from '@puzzlio/fields/tutorial';
import { tutorial as loopyRiverTutorial } from '@puzzlio/loopy-river/tutorial';
import { tutorial as mosaicTutorial } from '@puzzlio/mosaic/tutorial';

/*
  The game catalog, collected from each game package.

  Deliberately imports the `./config` subpath of each package rather than a barrel — the game
  packages expose subpaths only, so this never drags a grid component (and its CSS import) along
  with the metadata. That's what lets a React Native client read the catalog at all.
*/

export const GAME_META: Record<GameType, GameMeta> = {
  stars,
  fields,
  'loopy-river': loopyRiver,
  mosaic,
};

/** Each game's rules, as the How to Play panel states them. Data only — see `HowToPlay`. */
export const HOW_TO_PLAY: Record<GameType, HowToPlay> = {
  stars: starsHowToPlay,
  fields: fieldsHowToPlay,
  'loopy-river': loopyRiverHowToPlay,
  mosaic: mosaicHowToPlay,
};

/**
 * Each game's guided tutorial. Data only — see `Tutorial`.
 *
 * Typed loosely because each tutorial is written against its own game's board and cells; a caller
 * pairs one with a play of that same game, which is what makes the steps' types line up.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TUTORIALS: Record<GameType, Tutorial<any, any>> = {
  stars: starsTutorial,
  fields: fieldsTutorial,
  'loopy-river': loopyRiverTutorial,
  mosaic: mosaicTutorial,
};

/** Every game, in the order players see them. */
export const ALL_GAMES: GameMeta[] = [stars, fields, loopyRiver, mosaic];

/** Games a player can actually open — everything except those not yet playable by anyone. */
export const PLAYABLE_GAMES: GameMeta[] = ALL_GAMES.filter((game) => game.availability !== 'comingSoon');

/** Undefined for a slug that isn't a game — a deep link or a URL can name anything. */
export function findGame(slug: string | undefined): GameMeta | undefined {
  return ALL_GAMES.find((game) => game.type === slug);
}

/** Whether the given board slug is one of the game's practice-collection puzzles. */
export function isPracticeBoard(game: GameType, slug: string | null | undefined): boolean {
  if (!slug) return false;
  return GAME_META[game].practiceCollections.some((collection) => collection.puzzles.includes(slug));
}

/** URL of a practice puzzle board given its slug. */
export function practicePuzzleURL(game: GameType, slug: string): string {
  return `${GAME_META[game].url}/puzzle/${slug}`;
}
